import { useCallback, useEffect, useState, useRef } from 'react';
import { Modal } from './Modal';
import { netlifyStore } from '~/lib/stores/netlify';
import { useNetlifyLogs } from '~/lib/hooks/useNetlifyLogs';
import { useNetlifyAuth } from '~/lib/hooks/useNetlifyAuth';
import { useNetlifyDeployment } from '~/lib/hooks/useNetlifyDeployment';
import { useNetlifyStatus } from '~/lib/hooks/useNetlifyStatus';
import {
  AuthSection,
  UserProfile,
  DeployConfirmation,
  DeploymentProgress,
  SuccessDisplay,
  ErrorDisplay,
} from './netlify';
import { Tooltip } from './Tooltip';
import { setDeploymentStatus } from '~/lib/stores/netlify';

interface DeployModalProps {
  isOpen: boolean;
  onClose: () => void;
  netlifyConfig: {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_CLIENT_SECRET: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
  };
}

const DeployModal = ({ isOpen, onClose, netlifyConfig }: DeployModalProps) => {
  // State to solve the circular dependency
  const [logsCleanupFn, setLogsCleanupFn] = useState<(() => void) | undefined>(undefined);

  // Ref to track connection timeout
  const connectTimeoutRef = useRef<number | null>(null);

  const {
    accessToken,
    user,
    isValidatingToken,
    isCheckingToken,
    tokenCheckCount,
    validateToken,
    handleDisconnect,
    initiateOAuthFlow,
    resetAuthState,
  } = useNetlifyAuth(netlifyConfig, logsCleanupFn);

  // Use store for deployedId to avoid state duplication
  const storeState = netlifyStore.get();
  const deployedId = storeState.deployedId;

  const { logs, isConnecting, connectToLogs, safeConnectToLogs, getLogsForErrorFix, cleanupLogs } = useNetlifyLogs(
    deployedId,
    accessToken,
  );

  // Store the cleanupLogs function in state for the auth hook to use
  useEffect(() => {
    console.log('[DeployModal] Effect to store the cleanupLogs function in state for the auth hook to use');
    setLogsCleanupFn(() => cleanupLogs);
  }, [cleanupLogs]);

  const {
    // These are now all from global store, no more local state
    confirmDeploy,
    startDeployment,
    checkExistingDeployment,
    handleRedeploy,
    setConfirmDeploy,
  } = useNetlifyDeployment(accessToken, cleanupLogs);

  const { deploymentStatus, deployedUrl, error, updateDeploymentStatus, getCurrentStep } = useNetlifyStatus(
    deployedId,
    accessToken,
  );

  // Effect to check for existing deployment when modal opens
  useEffect(() => {
    console.log('[DeployModal] Effect to check for existing deployment when modal opens');

    if (!isOpen) {
      return;
    }

    checkExistingDeployment();
  }, [isOpen]);

  // Effect to handle deployment when confirmDeploy is true
  useEffect(() => {
    console.log('[DeployModal] Effect to handle deployment when confirmDeploy is true', {
      isOpen,
      accessToken,
      confirmDeploy,
    });

    if (!isOpen || !accessToken || !confirmDeploy) {
      return;
    }

    const handleDeploy = async () => {
      try {
        const isValid = await validateToken();

        if (!isValid) {
          return;
        }

        await startDeployment();
      } catch (err) {
        console.error('Deployment error:', err);
      }
    };

    handleDeploy();

    setConfirmDeploy(false);
  }, [isOpen, confirmDeploy, accessToken]);

  // Automatically connect to logs when deployedId is set
  useEffect(() => {
    if (deployedId && accessToken && deploymentStatus === 'building') {
      console.log('Auto-connecting to logs after deployedId update.', {
        deployedId,
        accessToken,
        deploymentStatus,
      });

      // Clear any previous connect timeout
      if (connectTimeoutRef.current) {
        console.log('[DeployModal] Clearing previous connect timeout');
        clearTimeout(connectTimeoutRef.current);
      }

      // Debounce the connection to avoid multiple rapid connects
      connectTimeoutRef.current = window.setTimeout(() => {
        console.log('[DeployModal] Connecting to logs after timeout');

        safeConnectToLogs();
        connectTimeoutRef.current = null;
      }, 300);
    }

    // Clean up on unmount
    return () => {
      if (connectTimeoutRef.current) {
        console.log('[DeployModal] Cleaning up connect timeout');
        clearTimeout(connectTimeoutRef.current);
      }
    };
  }, [deployedId, accessToken, deploymentStatus, safeConnectToLogs]);

  // Process logs to update deployment status
  useEffect(() => {
    console.log('[DeployModal] Effect to process logs to update deployment status', {
      logs,
      deploymentStatus,
    });

    // Loop through logs only once
    for (const log of logs) {
      // Update to building step when section is "building"
      if (log.type === 'line' || log.type === 'start') {
        const currentStatus = netlifyStore.get().deploymentStatus;

        if (log.section === 'building' && currentStatus === 'new') {
          console.log('Deployment is now in building phase');
          setDeploymentStatus('building');
        }

        // Update to deploying step when section is "deploying"
        if (log.section === 'deploying' && currentStatus === 'building') {
          // We keep the status as 'building' in the store but update the UI step
          console.log('Deployment is now in deploying phase');
        }
      } else if (log.type === 'report') {
        console.log('[DeployModal] Updating deployment status to deploying');
        updateDeploymentStatus();
      } else {
        if (log.type !== 'info') {
          console.info('Log type not recognized:', log.type);
        }
      }
    }
  }, [logs, updateDeploymentStatus]);

  // Handler for the "Fix with Dualite" button
  const handleFixWithDualite = useCallback(async () => {
    if (!error) {
      return;
    }

    // Show loading state
    const button = document.querySelector('#fix-with-dualite-button');

    if (button) {
      button.innerHTML = '<div class="i-ph:circle-notch w-4 h-4 animate-spin mr-2"></div>Collecting logs...';
    }

    // If we don't have logs yet, connect and wait briefly
    if (logs.length === 0 && !isConnecting) {
      console.log('[DeployModal] No logs available for error fix, connecting to logs');
      safeConnectToLogs();

      // Wait for logs to be fetched
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } else {
      console.log('[DeployModal] Logs already available or connecting for error fix');
    }

    const buildingLogs = logs.filter((log) => log.section === 'building').map((log) => log.message);

    // Format logs for the prompt - only use building section logs
    const logsText =
      buildingLogs.length > 0
        ? `\nDeployment Logs (building section):\n${buildingLogs.join('\n')}`
        : '\nNo relevant deployment logs available.';

    // Create prompt and dispatch event
    const errorPrompt = `I'm encountering the following error when deploying to Netlify:
    
Error: ${error}
${logsText}

Please analyze this error and the deployment logs to provide the necessary code fixes to address it. Focus on fixing the deployment issue.`;

    const fixEvent = new CustomEvent('dualite-fix-error', {
      detail: { prompt: errorPrompt },
    });

    document.dispatchEvent(fixEvent);

    console.log('Dispatched error fix event for Netlify deployment error with filtered logs');

    // Close the modal
    onClose();
  }, [error, onClose, logs, isConnecting, safeConnectToLogs, getLogsForErrorFix]);

  const handleAbortConnection = () => {
    localStorage.removeItem('netlifyState');
    handleDisconnect();
    resetAuthState();

    const netlifyWindow = window.open('', 'netlify-auth');

    if (netlifyWindow) {
      netlifyWindow.close();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-full px-4 max-w-[530px]">
        <div className="bg-alpha-background-secondary rounded-xl border border-white/10 relative w-full">
          <div className="p-6">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h2 className="text-xl font-semibold text-white m-0">Deploy your project</h2>
                <p className="text-[#e7e7e7]/70 text-sm my-3">
                  Go live in a minute with Netlify, our deployment partner.
                </p>
              </div>

              <Tooltip content="Close">
                <button
                  onClick={onClose}
                  className="p-2 text-[#e7e7e7] hover:text-white bg-transparent 
                    border-none ro?unded-md hover:bg-white/10 transition-colors"
                >
                  <div className="i-ph:x w-4 h-4" />
                </button>
              </Tooltip>
            </div>

            {accessToken && user && <UserProfile user={user} onDisconnect={handleDisconnect} />}

            {accessToken && isValidatingToken && (
              <div className="flex items-center justify-center gap-2 mt-3 text-sm text-[#e7e7e7]/70">
                <div className="i-ph:circle-notch w-4 h-4 animate-spin" />
                <span>Validating Netlify token...</span>
              </div>
            )}

            <div className="h-px bg-white/10 my-6" />

            <div className="space-y-6">
              {!accessToken ? (
                <AuthSection
                  isCheckingToken={isCheckingToken}
                  tokenCheckCount={tokenCheckCount}
                  onConnect={initiateOAuthFlow}
                  onAbort={handleAbortConnection}
                />
              ) : (
                <div className="space-y-4">
                  {!confirmDeploy && (
                    <DeployConfirmation
                      hasExistingDeployment={storeState.hasExistingDeployment}
                      previousDeployUrl={storeState.previousDeployUrl}
                      adminUrl={storeState.adminUrl}
                      deploymentStatus={deploymentStatus}
                      onRedeploy={handleRedeploy}
                    />
                  )}

                  {(deploymentStatus === 'new' || deploymentStatus === 'building') && (
                    <DeploymentProgress
                      currentStep={getCurrentStep()}
                      deployedId={deployedId}
                      accessToken={accessToken}
                      deployStatus={deploymentStatus}
                      adminUrl={storeState.adminUrl}
                    />
                  )}

                  {deploymentStatus === 'ready' && deployedUrl && (
                    <SuccessDisplay
                      deployedUrl={deployedUrl}
                      adminUrl={storeState.adminUrl}
                      deployedId={deployedId}
                      accessToken={accessToken}
                      deployStatus={deploymentStatus}
                    />
                  )}

                  {deploymentStatus === 'error' && (
                    <ErrorDisplay
                      error={error}
                      adminUrl={storeState.adminUrl}
                      onFixWithDualite={handleFixWithDualite}
                      deployedId={deployedId}
                      accessToken={accessToken}
                      deployStatus={deploymentStatus}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeployModal;
